import { makeAutoObservable } from 'mobx';
import {DomainStore} from 'shared/store';
import {t} from 'shared/core';
import {types, endpoints} from 'main/tools';
import {MassUpdate} from 'main/stores/mass_updates';
import _ from 'lodash';
import {setupAutosaveDraft} from 'main/tools';
import {Employee} from 'main/stores/employees';
import {CustomField, CustomFieldGroup} from 'main/stores/custom_fields';
import Location from 'main/stores/locations/Location';
import Department from 'main/stores/departments/Department';
import {JOB_FIELDS, PERSONAL_FIELDS} from 'main/containers/mass_updates/fields';

class MassUpdateEditorState {
  store = new DomainStore();
  match;
  history;

  errors = {};
  addEmployeeErrors = {};
  massUpdate;
  addEmployeesModalOpen = false;
  publishConfirmationModalOpen = false;
  selectedEmployees = [];
  interactiveAgent = [];
  customFieldGroups = [];
  customFields = [];
  bulkEditField = null;
  bulkEditValue = null;
  bulkEditModalOpen = false;
  updatingIncludedFields = false;

  constructor() {
    makeAutoObservable(this, {
      store: false,
      history: false,
      interactiveAgent: false
    });
  }

  receiveProps({match, history}) {
    this.match = match;
    this.history = history;
  }

  setInteractiveAgent(agent) {
    this.interactiveAgent = agent;
  }

  openBulkEditModal(field) {
    this.bulkEditField = _.camelCase(field);
    this.bulkEditModalOpen = true;
    this.bulkEditValue = this.massUpdate.bulkEditValues[this.bulkEditField] ?? null;
  }

  closeBulkEditModal() {
    this.bulkEditModalOpen = false;
  }

  openAddEmployeesModal() {
    this.addEmployeesModalOpen = true;
  }

  closeAddEmployeesModal() {
    this.addEmployeesModalOpen = false;
  }

  openPublishConfirmationModal() {
    this.publishConfirmationModalOpen = true;
  }

  closePublishConfirmationModal() {
    this.publishConfirmationModalOpen = false;
  }

  async load() {
    await this.store._compose([
      endpoints.MASS_UPDATES.with(this.match.params.id),
      endpoints.EMPLOYEES.ALL,
      endpoints.LOCATIONS.ALL,
      endpoints.DEPARTMENTS
    ]);

    this.massUpdate = new MassUpdate(this.store._getSingle(types.MASS_UPDATE));
    this.employees = this.store._getAll(types.EMPLOYEE, Employee);
    this.locations = this.store._getAll(types.LOCATION, Location);
    this.departments = this.store._getAll(types.DEPARTMENT, Department);
    await this.loadCustomFields();

    if (!this.massUpdate.readOnly) {
      await setupAutosaveDraft(
        this.massUpdate,
        {id: this.massUpdate.id, type: types.MASS_UPDATE}
      );
    }

    this.transformNewPayRates();
  }

  async loadCustomFields() {
    await this.store._compose([
      endpoints.CUSTOM_FIELD_GROUPS.with(this.massUpdate.massUpdateType)
    ]);

    this.customFieldGroups = this.store._getAll(types.CUSTOM_FIELD_GROUP, CustomFieldGroup);
    this.customFields = this.store._getAll(types.CUSTOM_FIELD, CustomField);
  }

  transformNewPayRates() {
    if (this.massUpdate.newPayRates) {
      Object.entries(this.massUpdate.newPayRates).forEach(([employeeId, payRate]) => {
        this.massUpdate.newValues[employeeId] = {payRate};
      });
      delete this.massUpdate.newPayRates;
    }
  }

  updateValue(value, id, field) {
    if (!this.massUpdate.newValues[id]) {
      this.massUpdate.newValues[id] = {};
    }
    this.massUpdate.newValues[id][field] = value;
    this.massUpdate.autosaver.autosave();
  }

  updateMassUpdate(newValues) {
    this.massUpdate.merge(newValues);
    this.massUpdate.autosaver.autosave();
  }

  setBulkEditValue(value) {
    this.bulkEditValue = value;
  }

  async applyBulkEdit() {
    this.massUpdate.bulkEditValues[this.bulkEditField] = this.bulkEditValue;
    _.forEach(this.massUpdate.newValues, (values) => {
      delete values[this.bulkEditField];
    });
    await this.massUpdate.autosaver.autosave();
    await this.interactiveAgent.refresh();
    this.closeBulkEditModal();
  }

  async removeMassUpdateEmployee(model) {
    await this.store.destroy(model);

    delete this.massUpdate.newValues[model.id];
    this.massUpdate.autosaver.autosave();
  }

  async addEmployeesToMassUpdate() {
    const selectedEmployeeIds = _.map(this.selectedEmployees, 'id');
    const {errors} = await this.store.post(
      `${this.massUpdate.link('createMassUpdateEmployees')}?effective_date=${this.massUpdate.effectiveDate || ''}`,
      types.MASS_UPDATE_EMPLOYEE,
      {employeeIds: selectedEmployeeIds}
    );
    this.massUpdate.autosaver.autosave();

    this.addEmployeeErrors = errors;

    if (_.isEmpty(errors)) {
      this.interactiveAgent.refresh();
      this.closeAddEmployeesModal();
      this.selectedEmployees = [];
    }
  }

  async patchIncludedField(massUpdate) {
    await this.store.patch(massUpdate, types.MASS_UPDATE);
    await this.interactiveAgent.refresh();
    await this.massUpdate.autosaver.autosave();
  }

  async publishMassUpdate() {
    const {model, errors} = await this.store.post(
      this.massUpdate.link('publish'),
      types.MASS_UPDATE,
      this.massUpdate
    );

    this.errors = errors;

    if (model) {
      this.history.push('/');
    }

    this.closePublishConfirmationModal();
  }

  async addIncludedField(field) {
    this.updatingIncludedFields = true;
    const massUpdate = new MassUpdate({includedFields: this.massUpdate.includedFields.slice(), links: this.massUpdate.links});
    massUpdate.addIncludedField(field);
    await this.patchIncludedField(massUpdate);
    this.massUpdate.addIncludedField(field);
    this.updatingIncludedFields = false;
  }

  async removeIncludedField(field) {
    if (this.updatingIncludedFields) return;

    this.updatingIncludedFields = true;
    const massUpdate = new MassUpdate({includedFields: this.massUpdate.includedFields.slice(), links: this.massUpdate.links});
    massUpdate.removeIncludedField(field);
    await this.patchIncludedField(massUpdate);
    this.massUpdate.removeIncludedField(field);
    this.updatingIncludedFields = false;
  }

  get fields() {
    const defaultFields = _.map(
      this.defaultFields,
      field => {
        return {
          id: field,
          name: t(`mass_updates.edit.fields.${field}`),
          groupId: 'default'
        };
      }
    );

    const customFields = _.chain(this.customFieldGroups)
      .sortBy('order')
      .flatMap(group => group.sortedCustomFields)
      .map(
        customField => {
          return {
            id: `cf${customField.id}`,
            name: customField.name,
            groupId: customField.customFieldGroupId};
        }
      )
      .value();

    return _.concat(defaultFields, customFields);
  }

  get defaultFields() {
    switch (this.massUpdate.massUpdateType) {
      case 'job':
        return JOB_FIELDS;
      case 'personal':
        return PERSONAL_FIELDS;
      default:
        return [];
    }
  }

  get fieldGroups() {
    const defaultGroup = [{id: 'default', name: t(`mass_updates.edit.${this.massUpdate.massUpdateType}`), order: -1}];

    return _.chain(defaultGroup)
      .concat(_.filter(this.customFieldGroups, group => group.customFields.length > 0))
      .sortBy('order')
      .value();
  }
}

export default MassUpdateEditorState;
